// Pure helpers for cursor pagination of archive calls. Rows are keyed by `id`;
// polling refetches page 1 while later pages stay cached, so the same record
// can arrive twice. The first occurrence wins, which keeps newest-first order.

import type { ArchiveAmbiguity, ArchiveCall, ArchiveCallsResponse, ArchiveCoverage } from './types';

export interface MergedArchivePages {
  readonly rows: readonly ArchiveCall[];
  readonly duplicatesDropped: number;
  // Coverage, ambiguity and truncation come from the newest page 1.
  readonly coverage: ArchiveCoverage | null;
  readonly ambiguity: ArchiveAmbiguity | null;
  readonly truncated: boolean;
  readonly nextCursor: string | null;
}

export function mergeArchivePages(pages: readonly ArchiveCallsResponse[]): MergedArchivePages {
  const seen = new Set<string>();
  const rows: ArchiveCall[] = [];
  let duplicatesDropped = 0;
  for (const page of pages) {
    for (const row of page.items) {
      if (seen.has(row.id)) {
        duplicatesDropped += 1;
        continue;
      }
      seen.add(row.id);
      rows.push(row);
    }
  }
  const first = pages[0] ?? null;
  const last = pages[pages.length - 1] ?? null;
  return {
    rows,
    duplicatesDropped,
    coverage: first?.coverage ?? null,
    ambiguity: first?.ambiguity ?? null,
    truncated: pages.some((page) => page.response.truncated || page.responseTruncated === true),
    nextCursor: last?.nextCursor ?? null,
  };
}
